import { Maybe } from '@csp/csp-common-model';
import { ScheduledConsentActionStatusTypeV1 } from '@csp/dto';
import { first } from 'lodash';
import { useMemo } from 'react';
import { useConsentContext } from '../context/hook/useConsentContext';
import { PendingVideoTrainingConsent } from '../model/PendingVideoTrainingConsent';
import { VideoTrainingConsent } from '../model/VideoTrainingConsent';
import { mapUserRolesToVideoTrainingConsentTypes } from '../util/mapUserRolesToVideoTrainingConsentTypes';
import { useCurrentUser } from '../../security/hooks/useCurrentUser';

type Out = {
  consent: Maybe<VideoTrainingConsent>;
  isLoading: boolean;
  pendingConsent: Maybe<PendingVideoTrainingConsent>;
};

/**
 * Hook for accessing formatted video training consent data from context.
 * @returns {Out} - Object containing loading state, first consent and first pending consent.
 */
export const useVideoTrainingConsent = (): Out => {
  const { consents, isLoading, hasOutdatedVideoTrainingConsents } = useConsentContext();
  const user = useCurrentUser();

  const videoTrainingConsents = useMemo(() => {
    const types = mapUserRolesToVideoTrainingConsentTypes(user.roles ?? []);

    return consents.filter(consent => types.includes(consent.consentType));
  }, [consents, user.roles]);

  const firstConsent = useMemo(
    () =>
      first(
        videoTrainingConsents.filter(consent => consent.approvalStatus === ScheduledConsentActionStatusTypeV1.APPROVED),
      ),
    [videoTrainingConsents],
  );

  const firstPendingConsent = useMemo(
    () =>
      first(
        videoTrainingConsents.filter(consent => consent.approvalStatus === ScheduledConsentActionStatusTypeV1.PENDING),
      ),
    [videoTrainingConsents],
  );

  return useMemo(
    () => ({
      isLoading,
      consent: firstConsent
        ? {
            artifactCode: firstConsent.artifactCode,
            consentType: firstConsent.consentType,
            version: firstConsent.version,
          }
        : undefined,
      pendingConsent: firstPendingConsent
        ? {
            approveConsent: async () => await firstPendingConsent.approveConsent(),
            artifactCode: firstPendingConsent?.artifactCode,
            consentType: firstPendingConsent.consentType,
            hasOutdatedConsents: hasOutdatedVideoTrainingConsents(),
            version: firstPendingConsent.version,
          }
        : undefined,
    }),
    [isLoading, firstConsent, firstPendingConsent, hasOutdatedVideoTrainingConsents],
  );
};
